import { createStore } from 'vuex'
import { createEntityId, createNestedId, getEntityPrefix } from '../helpers/id'
import { createFirebaseDataRepository } from '../services/data/firebaseDataRepository'
import { loginWithEmail, logout, subscribeToAuthState } from '../services/firebase/authService'
import { appendActionLog } from '../services/firebase/logsService'
import { ADMIN_PANEL_ROLES, fetchUser } from '../services/firebase/usersService'

const NOTIFICATION_TYPES = ['success', 'warning', 'error', 'info']
const dataRepository = createFirebaseDataRepository()
let authInitPromise = null

function canAccessAdmin(profile) {
  return Boolean(profile && !profile.isBlocked && ADMIN_PANEL_ROLES.includes(profile.role))
}

function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}

function normalizeCourseParams(course) {
  if (!course) return
  const stats = Array.isArray(course.stats) ? course.stats : []
  const isEmpty = value => {
    if (value === undefined || value === null) return true
    if (typeof value === 'string') return value === ''
    if (typeof value === 'object') return ['ua', 'ru', 'en'].every(lang => !value[lang])
    return false
  }
  const params = [
    ['courseDuration', stats[0] && stats[0].value],
    ['courseLessons', (stats[1] && stats[1].value) || course.duration],
    ['coursePrice', (stats[2] && stats[2].value) || course.price],
    ['courseFormat', (stats[3] && stats[3].value) || course.format]
  ]

  params.forEach(([key, fallback]) => {
    if (!isEmpty(course[key])) return
    if (fallback && typeof fallback === 'object') {
      course[key] = fallback
      return
    }
    course[key] = { ua: fallback || '', ru: fallback || '', en: fallback || '' }
  })

  delete course.duration
  delete course.price
  delete course.format
  delete course.stats
}

function normalizeCourseTeachers(course) {
  if (!course) return
  if (!Array.isArray(course.teachers) || !course.teachers.length) {
    course.teachers = course.authorId ? [{
      id: createNestedId(course.id, 'teachers'),
      memberId: course.authorId
    }] : []
  }
}

function ensureDbShape(db) {
  if (!db.categories) db.categories = {}
  if (!Array.isArray(db.categories.blogCategories)) db.categories.blogCategories = []
  if (!db.collections) db.collections = {}
  if (!db.collections.blogs) db.collections.blogs = { items: [] }
  if (!db.collections.additionalPages) db.collections.additionalPages = { items: [] }
  if (!db.collections.startedCourses) db.collections.startedCourses = { items: [] }
  if (!db.collections.testBank) db.collections.testBank = { items: [] }
  if (!db.collections.testAttempts) db.collections.testAttempts = { items: [] }
}

function remapCategoryValue(value, map) {
  if (Array.isArray(value)) return value.map(categoryId => map[categoryId] || categoryId)
  return map[value] || value
}

function hasEntityPrefix(id, type) {
  return typeof id === 'string' && id.startsWith(`${getEntityPrefix(type)}-`)
}

function normalizeIdsInList(list, type) {
  const map = {}
  ;(list || []).forEach(item => {
    if (!item || hasEntityPrefix(item.id, type)) return
    const previousId = item.id
    item.id = createEntityId(type)
    if (previousId) map[previousId] = item.id
  })
  return map
}

function normalizeNestedIds(collection) {
  const groups = collection.groups || []
  ;(collection.items || []).forEach(item => {
    groups.forEach(group => {
      if (!Array.isArray(item[group.key])) return
      item[group.key].forEach(nestedItem => {
        if (!nestedItem.id) nestedItem.id = createNestedId(item.id, group.key)
      })
    })
  })
}

function normalizeDbEntityIds(db) {
  ensureDbShape(db)
  const courseCategoryMap = normalizeIdsInList(db.categories && db.categories.courseCategories, 'courseCategories')
  const eventCategoryMap = normalizeIdsInList(db.categories && db.categories.eventCategories, 'eventCategories')
  const blogCategoryMap = normalizeIdsInList(db.categories && db.categories.blogCategories, 'blogCategories')
  normalizeIdsInList(db.collections && db.collections.courses && db.collections.courses.items, 'courses')
  normalizeIdsInList(db.collections && db.collections.startedCourses && db.collections.startedCourses.items, 'startedCourses')
  normalizeIdsInList(db.collections && db.collections.testBank && db.collections.testBank.items, 'testBank')
  normalizeIdsInList(db.collections && db.collections.testAttempts && db.collections.testAttempts.items, 'testAttempts')
  normalizeIdsInList(db.collections && db.collections.events && db.collections.events.items, 'events')
  normalizeIdsInList(db.collections && db.collections.blogs && db.collections.blogs.items, 'blogs')
  normalizeIdsInList(db.collections && db.collections.additionalPages && db.collections.additionalPages.items, 'additionalPages')
  const teamMap = normalizeIdsInList(db.collections && db.collections.team && db.collections.team.items, 'team')

  ;((db.collections && db.collections.courses && db.collections.courses.items) || []).forEach(course => {
    course.category = remapCategoryValue(course.category, courseCategoryMap)
    if (teamMap[course.authorId]) course.authorId = teamMap[course.authorId]
    const teachers = course.teachers || []
    teachers.forEach(teacher => {
      if (teamMap[teacher.memberId]) teacher.memberId = teamMap[teacher.memberId]
    })
  })

  ;((db.collections && db.collections.events && db.collections.events.items) || []).forEach(event => {
    if ((!event.speakers || !event.speakers.length) && (event.speaker || event.speakerInfo || event.speakerImage)) {
      event.speakers = [{
        memberId: '',
        customName: event.speaker || { ua: '', ru: '', en: '' },
        customInfo: event.speakerInfo || { ua: '', ru: '', en: '' },
        topic: { ua: '', ru: '', en: '' },
        image: event.speakerImage || ''
      }]
    }
    event.category = remapCategoryValue(event.category, eventCategoryMap)
    ;(event.speakers || []).forEach(speaker => {
      if (teamMap[speaker.memberId]) speaker.memberId = teamMap[speaker.memberId]
    })
  })

  ;((db.collections && db.collections.blogs && db.collections.blogs.items) || []).forEach(article => {
    article.category = remapCategoryValue(article.category, blogCategoryMap)
    if (!Array.isArray(article.comments)) article.comments = []
    article.comments.forEach(comment => {
      if (!comment.id) comment.id = createNestedId(article.id, 'comments')
    })
  })

  Object.keys((db.collections || {})).forEach(collectionKey => {
    normalizeNestedIds(db.collections[collectionKey])
  })

  ;((db.collections && db.collections.courses && db.collections.courses.items) || []).forEach(course => {
    normalizeCourseParams(course)
    normalizeCourseTeachers(course)
  })
}

function createNotification(payload = {}) {
  const type = NOTIFICATION_TYPES.includes(payload.type) ? payload.type : 'success'
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    title: payload.title || '',
    message: payload.message || '',
    duration: typeof payload.duration === 'number' ? payload.duration : 4200
  }
}

function collectionItemTitle(item) {
  if (!item) return ''
  const title = item.title || item.name || item.uid || item.id
  if (title && typeof title === 'object') {
    return title.ua || title.ru || title.en || Object.values(title).find(Boolean) || ''
  }
  return title ? String(title) : ''
}

export default createStore({
  state: {
    isLoading: false,
    activeLang: 'ua',
    db: null,
    error: '',
    authReady: false,
    authUser: null,
    authProfile: null,
    authError: '',
    notifications: []
  },
  getters: {
    isLoading: state => state.isLoading,
    activeLang: state => state.activeLang,
    db: state => state.db,
    pages: state => state.db ? state.db.pages : {},
    general: state => state.db ? state.db.general : {},
    collections: state => state.db ? state.db.collections : {},
    categories: state => state.db ? state.db.categories : {},
    authReady: state => state.authReady,
    authUser: state => state.authUser,
    authProfile: state => state.authProfile,
    isAuthenticated: state => Boolean(state.authUser && canAccessAdmin(state.authProfile)),
    isAdmin: state => Boolean(state.authProfile && state.authProfile.role === 'Admin' && !state.authProfile.isBlocked),
    notifications: state => state.notifications
  },
  mutations: {
    setLoading(state, value) {
      state.isLoading = value
    },
    setError(state, value) {
      state.error = value
    },
    setLang(state, lang) {
      state.activeLang = lang
    },
    setDb(state, db) {
      state.db = db
    },
    updateDb(state, db) {
      state.db = db
    },
    setAuthReady(state, value) {
      state.authReady = value
    },
    setAuthUser(state, user) {
      state.authUser = user
        ? {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName || ''
          }
        : null
    },
    setAuthProfile(state, profile) {
      state.authProfile = profile
    },
    setAuthError(state, value) {
      state.authError = value
    },
    addNotification(state, notification) {
      state.notifications.unshift(notification)
    },
    removeNotification(state, notificationId) {
      state.notifications = state.notifications.filter(item => item.id !== notificationId)
    }
  },
  actions: {
    notify({ commit }, payload) {
      const notification = createNotification(payload)
      commit('addNotification', notification)
      if (notification.duration > 0) {
        window.setTimeout(() => {
          commit('removeNotification', notification.id)
        }, notification.duration)
      }
      return Promise.resolve(notification.id)
    },
    dismissNotification({ commit }, notificationId) {
      commit('removeNotification', notificationId)
      return Promise.resolve()
    },
    recordAuditLog({ dispatch, state }, payload = {}) {
      const profile = state.authProfile || {}
      const user = state.authUser || {}
      return appendActionLog({
        ...payload,
        actor: {
          uid: user.uid || profile.uid || '',
          name: profile.name || user.displayName || '',
          role: profile.role || ''
        },
        route: window.location.hash || window.location.pathname || ''
      }).catch(error => {
        // eslint-disable-next-line no-console
        console.error('Не удалось записать действие в журнал Storage.', {
          code: error && error.code,
          message: error && error.message,
          action: payload.action,
          entityType: payload.entityType
        })
        return dispatch('notify', {
          type: 'warning',
          title: 'Действие сохранено без записи в журнал',
          message: error && error.message
            ? error.message
            : 'Не удалось обновить файл логов в Storage.'
        }).then(() => null)
      })
    },
    initAuth({ commit, state }) {
      if (state.authReady) return Promise.resolve(state.authUser)
      if (authInitPromise) return authInitPromise

      authInitPromise = new Promise(resolve => {
        subscribeToAuthState(async user => {
          commit('setAuthUser', user)
          if (!user) {
            commit('setAuthProfile', null)
            commit('setAuthReady', true)
            resolve(null)
            return
          }

          const profile = await fetchUser(user.uid)
          if (!canAccessAdmin(profile)) {
            await logout()
            commit('setAuthUser', null)
            commit('setAuthProfile', null)
            commit('setAuthReady', true)
            resolve(null)
            return
          }

          commit('setAuthProfile', profile)
          commit('setAuthReady', true)
          resolve(user)
        })
      })

      return authInitPromise
    },
    login({ commit }, { email, password }) {
      commit('setAuthError', '')
      return loginWithEmail(email, password)
        .then(async user => {
          const profile = await fetchUser(user.uid)
          if (!canAccessAdmin(profile)) {
            await logout()
            throw new Error('У пользователя нет доступа к админке или аккаунт заблокирован.')
          }
          commit('setAuthUser', user)
          commit('setAuthProfile', profile)
          return user
        })
        .catch(error => {
          commit('setAuthError', error.message)
          return Promise.reject(error)
        })
    },
    logout({ commit }) {
      return logout()
        .then(() => {
          commit('setAuthUser', null)
          commit('setAuthProfile', null)
        })
    },
    fetchDb({ commit, dispatch }) {
      commit('setLoading', true)

      return dataRepository.loadDb()
        .then(db => {
          normalizeDbEntityIds(db)
          commit('setDb', db)
        })
        .catch(error => {
          commit('setError', error.message)
          return dispatch('notify', {
            type: 'error',
            title: 'Ошибка загрузки данных',
            message: error.message || 'Не удалось загрузить данные из Firestore.'
          }).then(() => Promise.reject(error))
        })
        .finally(() => commit('setLoading', false))
    },
    saveGeneral({ dispatch, state }, content) {
      try {
        state.db.general = clone(content)
        return dataRepository.saveGeneral(state.db.general)
          .then(savedGeneral => {
            state.db.general = clone(savedGeneral)
            return savedGeneral
          })
          .then(result => {
            return dispatch('recordAuditLog', {
              action: 'save_general',
              entityType: 'general',
              title: 'Общие настройки'
            }).then(() => result)
          })
          .then(result => dispatch('notify', {
            type: 'success',
            title: 'Общие настройки сохранены'
          }).then(() => result))
      } catch (error) {
        return dispatch('notify', {
          type: 'error',
          title: 'Ошибка сохранения',
          message: error.message || 'Не удалось сохранить общие настройки.'
        }).then(() => Promise.reject(error))
      }
    },
    saveCategories({ dispatch, state }, { categoryKey, title } = {}) {
      try {
        const categories = clone(state.db.categories || {})
        const entries = categoryKey
          ? [[categoryKey, categories[categoryKey] || []]]
          : Object.entries(categories)
        return Promise.all(entries.map(([nextCategoryKey, items]) => {
          return dataRepository.saveCategoryGroup(nextCategoryKey, items)
            .then(savedItems => [nextCategoryKey, savedItems])
        }))
          .then(savedEntries => {
            savedEntries.forEach(([categoryKey, savedItems]) => {
              state.db.categories[categoryKey] = clone(savedItems)
              categories[categoryKey] = clone(savedItems)
            })
            return categories
          })
          .then(result => {
            return dispatch('recordAuditLog', {
              action: 'save_categories',
              entityType: 'categories',
              title: title || 'Категории'
            }).then(() => result)
          })
          .then(result => dispatch('notify', {
            type: 'success',
            title: title || 'Категории сохранены'
          }).then(() => result))
      } catch (error) {
        return dispatch('notify', {
          type: 'error',
          title: 'Ошибка сохранения',
          message: error.message || 'Не удалось сохранить категории.'
        }).then(() => Promise.reject(error))
      }
    },
    deleteCategoryItem({ dispatch, state }, { categoryKey, itemId }) {
      try {
        const list = state.db.categories[categoryKey] || []
        const index = list.findIndex(item => item.id === itemId)
        if (index >= 0) list.splice(index, 1)
        return dataRepository.deleteCategoryItem(categoryKey, itemId)
          .then(result => {
            return dispatch('recordAuditLog', {
              action: 'delete_category_item',
              entityType: 'category',
              entityId: itemId,
              title: categoryKey,
              details: { categoryKey }
            }).then(() => result)
          })
          .then(result => dispatch('notify', {
            type: 'success',
            title: 'Категория удалена'
          }).then(() => result))
      } catch (error) {
        return dispatch('notify', {
          type: 'error',
          title: 'Ошибка удаления',
          message: error.message || 'Не удалось удалить категорию.'
        }).then(() => Promise.reject(error))
      }
    },
    createCollectionItem({ dispatch, state }, { collectionKey, item }) {
      try {
        const collection = state.db.collections[collectionKey]
        if (!collection.items.find(existingItem => existingItem.id === item.id)) {
          collection.items.unshift(clone(item))
        }
        return dataRepository.saveCollectionItem(collectionKey, item)
          .then(savedItem => {
            return savedItem
          })
          .then(result => {
            return dispatch('recordAuditLog', {
              action: 'create_collection_item',
              entityType: collectionKey,
              entityId: item.id,
              title: collectionItemTitle(item),
              details: { collectionKey }
            }).then(() => result)
          })
          .then(result => dispatch('notify', {
            type: 'success',
            title: 'Запись создана'
          }).then(() => result))
      } catch (error) {
        return dispatch('notify', {
          type: 'error',
          title: 'Ошибка создания',
          message: error.message || 'Не удалось создать запись.'
        }).then(() => Promise.reject(error))
      }
    },
    updateCollectionItem({ dispatch, state }, { collectionKey, item }) {
      try {
        const collection = state.db.collections[collectionKey]
        const index = collection.items.findIndex(existingItem => existingItem.id === item.id)
        if (index >= 0) collection.items.splice(index, 1, clone(item))
        return dataRepository.saveCollectionItem(collectionKey, item)
          .then(savedItem => {
            return savedItem
          })
          .then(result => {
            return dispatch('recordAuditLog', {
              action: 'update_collection_item',
              entityType: collectionKey,
              entityId: item.id,
              title: collectionItemTitle(item),
              details: { collectionKey }
            }).then(() => result)
          })
          .then(result => dispatch('notify', {
            type: 'success',
            title: 'Изменения сохранены'
          }).then(() => result))
      } catch (error) {
        return dispatch('notify', {
          type: 'error',
          title: 'Ошибка сохранения',
          message: error.message || 'Не удалось сохранить запись.'
        }).then(() => Promise.reject(error))
      }
    },
    deleteCollectionItem({ dispatch, state }, { collectionKey, itemId }) {
      try {
        const collection = state.db.collections[collectionKey]
        const index = collection.items.findIndex(item => item.id === itemId)
        if (index >= 0) collection.items.splice(index, 1)
        return dataRepository.deleteCollectionItem(collectionKey, itemId)
          .then(result => {
            return result
          })
          .then(result => {
            return dispatch('recordAuditLog', {
              action: 'delete_collection_item',
              entityType: collectionKey,
              entityId: itemId,
              details: { collectionKey }
            }).then(() => result)
          })
          .then(result => dispatch('notify', {
            type: 'success',
            title: 'Запись удалена'
          }).then(() => result))
      } catch (error) {
        return dispatch('notify', {
          type: 'error',
          title: 'Ошибка удаления',
          message: error.message || 'Не удалось удалить запись.'
        }).then(() => Promise.reject(error))
      }
    },
    reloadDb({ commit, dispatch }) {
      return dataRepository.resetDb()
        .then(db => {
          normalizeDbEntityIds(db)
          commit('setDb', db)
          return db
        })
        .then(result => dispatch('notify', {
          type: 'warning',
          title: 'Данные обновлены из Firestore'
        }).then(() => result))
    },
    exportDb({ state, dispatch }) {
      downloadJson('firestore-backup.json', state.db)
      return dispatch('notify', {
        type: 'success',
        title: 'Резервная копия Firestore экспортирована'
      })
    },
    savePage({ dispatch, state }, { pageKey, content }) {
      try {
        state.db.pages[pageKey] = clone(content)
        return dataRepository.savePage(pageKey, state.db.pages[pageKey])
          .then(savedPages => {
            state.db.pages = clone(savedPages)
            return savedPages[pageKey]
          })
          .then(result => {
            return dispatch('recordAuditLog', {
              action: 'save_page',
              entityType: 'page',
              entityId: pageKey,
              title: pageKey,
              details: { pageKey }
            }).then(() => result)
          })
          .then(result => dispatch('notify', {
            type: 'success',
            title: 'Страница сохранена'
          }).then(() => result))
      } catch (error) {
        return dispatch('notify', {
          type: 'error',
          title: 'Ошибка сохранения страницы',
          message: error.message || 'Не удалось сохранить страницу.'
        }).then(() => Promise.reject(error))
      }
    }
  }
})
